import {
  DeviceSyncField,
  type DeviceSyncResponse,
  DEFAULT_DEVICE_SYNC_FIELDS,
} from '@am3nd-org/pointcraft-types';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';
import { DeviceSyncResponseCompatibilitySchema } from 'src/utils/operator-authentication-access';

import { useDeviceContextStore } from 'src/store/DeviceContext';
import { useDeviceSyncStore, type DeviceSyncVersions } from 'src/store/DeviceSync';
import {
  getSessionGeneration,
  registerSessionCleanup,
  isSessionGenerationCurrent,
} from 'src/auth/session-lifecycle';

import {
  clearProviderCredentials,
  replaceProviderCredentials,
} from './provider-credentials.service';

export type DeviceSyncV4Field = DeviceSyncField;
export type DeviceSyncV4Payload = DeviceSyncResponse;
export type DeviceSyncV4Meta = DeviceSyncVersions;

export type BusinessDeviceSyncFields = {
  businessProfile?: unknown;
  userProfile?: unknown;
  terminals?: unknown;
  businessDevices?: unknown;
  providerDetails?: unknown;
  recurringPlans?: unknown;
};

const FIRESTORE_TO_SYNC_FIELD = {
  businessProfile: DeviceSyncField.BUSINESS_DATA,
  userProfile: DeviceSyncField.USER_DATA,
  terminals: DeviceSyncField.TERMINALS,
  businessDevices: DeviceSyncField.BUSINESS_DEVICES,
  providerDetails: DeviceSyncField.PROVIDER_DETAILS,
  recurringPlans: DeviceSyncField.RECURRING_PLANS,
} as const;

const SYNC_FIELD_TO_RESPONSE_KEY: Record<DeviceSyncField, keyof DeviceSyncResponse> = {
  [DeviceSyncField.APPLICATION_DEVICE_CONTEXT]: 'applicationDeviceContext',
  [DeviceSyncField.BUSINESS_DATA]: 'businessData',
  [DeviceSyncField.USER_DATA]: 'userData',
  [DeviceSyncField.TERMINALS]: 'terminals',
  [DeviceSyncField.BUSINESS_DEVICES]: 'businessDevices',
  [DeviceSyncField.PROVIDER_DETAILS]: 'providerDetails',
  [DeviceSyncField.PROVIDER_CREDENTIALS]: 'providerCredentials',
  [DeviceSyncField.RECURRING_PLANS]: 'recurringPlans',
};

let activeSync: Promise<void> | null = null;
let queuedFields = new Set<DeviceSyncField>();
let queuedVersions: DeviceSyncVersions = {};
let recentNetworkSync: { fields: Set<DeviceSyncField>; recordedAt: number } | null = null;

const RECENT_NETWORK_SYNC_WINDOW_MS = 30_000;

registerSessionCleanup(() => {
  queuedFields.clear();
  queuedVersions = {};
  recentNetworkSync = null;
});

function normalizeFields(fields?: DeviceSyncField[]): DeviceSyncField[] {
  const requested = fields?.length ? fields : DEFAULT_DEVICE_SYNC_FIELDS;
  return Array.from(new Set(requested));
}

function hasOwn(value: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function readVersion(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { version } = value as { version?: unknown };
  return typeof version === 'number' ? version : undefined;
}

function buildDeviceSyncUrl(fields: DeviceSyncField[]): string {
  const params = new URLSearchParams({ fields: fields.join(',') });
  return `${apiEndpoints.v4.deviceSync}?${params.toString()}`;
}

export async function fetchDeviceSyncPayload(
  fields: DeviceSyncField[]
): Promise<DeviceSyncResponse> {
  const result = await apiSenderV2(buildDeviceSyncUrl(fields), ApiMethods.GET, null, true, null, {
    throwOnError: true,
    timeout: 30_000,
  });
  const parsed = DeviceSyncResponseCompatibilitySchema.safeParse(result);

  if (!parsed.success) {
    throw new Error(`[DeviceSyncV4] Invalid response: ${parsed.error.message}`);
  }

  const missingFields = fields.filter(
    (field) => !hasOwn(parsed.data, SYNC_FIELD_TO_RESPONSE_KEY[field])
  );
  if (missingFields.length) {
    throw new Error(`[DeviceSyncV4] Response missing fields: ${missingFields.join(', ')}`);
  }

  return parsed.data;
}

async function applyDeviceSyncPayload(payload: DeviceSyncResponse): Promise<void> {
  const currentTenantId = useDeviceSyncStore.getState().tenantId;
  const responseTenantId =
    payload.businessData?.id ?? payload.applicationDeviceContext?.businessId ?? currentTenantId;

  if (currentTenantId && responseTenantId && currentTenantId !== responseTenantId) {
    useDeviceSyncStore.getState().clearTenantData();
    useDeviceContextStore.getState().reset();
    await clearProviderCredentials();
  }

  if (payload.applicationDeviceContext) {
    useDeviceContextStore.getState().setApplicationDeviceContext(payload.applicationDeviceContext);
    useDeviceContextStore.getState().setStatus('ready');
  }

  if (payload.providerCredentials) {
    await replaceProviderCredentials(payload.providerCredentials);
  }

  const safeSlices: DeviceSyncResponse = {
    ...(payload.businessData !== undefined ? { businessData: payload.businessData } : {}),
    ...(payload.userData !== undefined ? { userData: payload.userData } : {}),
    ...(payload.terminals !== undefined ? { terminals: payload.terminals } : {}),
    ...(payload.businessDevices !== undefined ? { businessDevices: payload.businessDevices } : {}),
    ...(payload.providerDetails !== undefined ? { providerDetails: payload.providerDetails } : {}),
    ...(payload.recurringPlans !== undefined ? { recurringPlans: payload.recurringPlans } : {}),
  };

  if (Object.keys(safeSlices).length) {
    useDeviceSyncStore.getState().applySlices(safeSlices);
  }
}

function markRecentNetworkSync(fields: DeviceSyncField[]): void {
  const previous =
    recentNetworkSync && Date.now() - recentNetworkSync.recordedAt < RECENT_NETWORK_SYNC_WINDOW_MS
      ? recentNetworkSync.fields
      : new Set<DeviceSyncField>();

  recentNetworkSync = {
    fields: new Set([...Array.from(previous), ...fields]),
    recordedAt: Date.now(),
  };
}

function recentlyFetched(field: DeviceSyncField): boolean {
  if (!recentNetworkSync) return false;
  if (Date.now() - recentNetworkSync.recordedAt > RECENT_NETWORK_SYNC_WINDOW_MS) {
    recentNetworkSync = null;
    return false;
  }
  return recentNetworkSync.fields.has(field);
}

function queueSync(fields: DeviceSyncField[], versions: DeviceSyncVersions): void {
  fields.forEach((field) => queuedFields.add(field));
  queuedVersions = { ...queuedVersions, ...versions };
}

async function fetchAndApply(
  fields: DeviceSyncField[],
  versions: DeviceSyncVersions
): Promise<void> {
  const generation = getSessionGeneration();
  const store = useDeviceSyncStore.getState();
  store.setStatus('syncing');

  try {
    const payload = await fetchDeviceSyncPayload(fields);
    if (!isSessionGenerationCurrent(generation)) return;
    await applyDeviceSyncPayload(payload);
    if (!isSessionGenerationCurrent(generation)) return;
    store.setVersions(versions);
    store.setStatus('ready');
    markRecentNetworkSync(fields);
  } catch (error) {
    if (!isSessionGenerationCurrent(generation)) return;
    store.setStatus('error', error instanceof Error ? error.message : 'Device sync failed');
    throw error;
  }
}

export async function syncDeviceSyncV4Fields(
  fields: DeviceSyncField[] = DEFAULT_DEVICE_SYNC_FIELDS,
  remoteVersions: DeviceSyncVersions = {}
): Promise<void> {
  const normalizedFields = normalizeFields(fields);
  if (!normalizedFields.length) return;

  if (activeSync) {
    queueSync(normalizedFields, remoteVersions);
    await activeSync;
    return;
  }

  activeSync = (async () => {
    let nextFields = normalizedFields;
    let nextVersions = remoteVersions;

    while (nextFields.length) {
      const localVersions = useDeviceSyncStore.getState().versions;
      const iterationVersions = nextVersions;
      const firestoreEntries = Object.entries(FIRESTORE_TO_SYNC_FIELD) as Array<
        [keyof DeviceSyncVersions, DeviceSyncField]
      >;

      const adoptableVersions: DeviceSyncVersions = {};
      nextFields = nextFields.filter((field) => {
        const entry = firestoreEntries.find(([, mappedField]) => mappedField === field);
        if (!entry) return true;

        const [versionKey] = entry;
        const remoteVersion = iterationVersions[versionKey];
        if (
          typeof remoteVersion === 'number' &&
          remoteVersion !== localVersions[versionKey] &&
          recentlyFetched(field)
        ) {
          adoptableVersions[versionKey] = remoteVersion;
          return false;
        }
        return true;
      });

      if (Object.keys(adoptableVersions).length) {
        useDeviceSyncStore.getState().setVersions(adoptableVersions);
      }

      if (nextFields.length) {
        // Queued refreshes depend on the previous refresh having updated the store.
        // eslint-disable-next-line no-await-in-loop
        await fetchAndApply(nextFields, nextVersions);
      }

      nextFields = Array.from(queuedFields);
      nextVersions = queuedVersions;
      queuedFields = new Set<DeviceSyncField>();
      queuedVersions = {};
    }
  })().finally(() => {
    activeSync = null;
  });

  await activeSync;
}

export async function hydrateDeviceSyncV4FromCache(): Promise<boolean> {
  if (!useDeviceSyncStore.persist.hasHydrated()) {
    await useDeviceSyncStore.persist.rehydrate();
  }
  return Boolean(useDeviceSyncStore.getState().cachedAt);
}

export async function handleDeviceSyncBusinessFields(
  fields: BusinessDeviceSyncFields
): Promise<void> {
  const remoteVersions: DeviceSyncVersions = {
    businessProfile: readVersion(fields.businessProfile),
    userProfile: readVersion(fields.userProfile),
    terminals: readVersion(fields.terminals),
    businessDevices: readVersion(fields.businessDevices),
    providerDetails: readVersion(fields.providerDetails),
    recurringPlans: readVersion(fields.recurringPlans),
  };
  const localVersions = useDeviceSyncStore.getState().versions;

  const fieldsToSync = (
    Object.entries(FIRESTORE_TO_SYNC_FIELD) as Array<[keyof DeviceSyncVersions, DeviceSyncField]>
  )
    .filter(([versionKey]) => {
      const remoteVersion = remoteVersions[versionKey];
      return typeof remoteVersion === 'number' && remoteVersion !== localVersions[versionKey];
    })
    .map(([, field]) => field);

  if (!fieldsToSync.length) return;

  const isPairedDevice = Boolean(useDeviceContextStore.getState().getApplicationDeviceContext());
  const shouldRefreshProviderCredentials =
    isPairedDevice &&
    fieldsToSync.includes(DeviceSyncField.PROVIDER_DETAILS) &&
    !recentlyFetched(DeviceSyncField.PROVIDER_DETAILS);
  if (isPairedDevice && fieldsToSync.includes(DeviceSyncField.BUSINESS_DEVICES)) {
    fieldsToSync.push(DeviceSyncField.APPLICATION_DEVICE_CONTEXT);
  }

  await syncDeviceSyncV4Fields(Array.from(new Set(fieldsToSync)), remoteVersions);

  if (shouldRefreshProviderCredentials) {
    await syncDeviceSyncV4Fields([DeviceSyncField.PROVIDER_CREDENTIALS]);
  }
}
